import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useConnections } from '@/features/booking/api';
import { ensureNamespace } from '@/lib/i18n';
import { isSectionActive, PRODUCT_SECTIONS } from '@/lib/product-nav';

export const Route = createFileRoute('/app/booking')({
  loader: () => ensureNamespace('booking'),
  component: BookingLayout,
});

const SECTIONS = PRODUCT_SECTIONS.booking ?? [];

function BookingLayout() {
  const { t } = useTranslation(['booking', 'common']);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Sections are navigated from the sidebar (or the mobile drawer); the heading
  // names the current one so the page still says where you are.
  const section = SECTIONS.find((s) => isSectionActive(s, pathname));
  return (
    <div>
      <div className="mb-6 hidden md:block">
        <p className="text-xs font-medium tracking-wide text-neutral-500 uppercase">
          {t('common:products.booking.name')}
        </p>
        <h1 className="mt-0.5 text-lg font-semibold">
          {section ? t(`booking:nav.${section.key}`) : t('common:products.booking.name')}
        </h1>
      </div>
      <CalendarAlert pathname={pathname} />
      <Outlet />
    </div>
  );
}

/**
 * A broken calendar connection stops bookings outright, so it cannot be a
 * quiet badge on one page. This sits above every Booking screen until fixed.
 */
function CalendarAlert({ pathname }: { pathname: string }) {
  const { t } = useTranslation(['booking']);
  const { data: connections = [] } = useConnections();
  const broken = connections.filter((c) => c.status !== 'active');
  if (broken.length === 0) return null;

  // With one account to fix, start its provider flow from here: sending the
  // host to the Calendars page does nothing when the banner is already on it.
  // With several, only that page can say which is which.
  const only = broken.length === 1 ? broken[0] : undefined;
  const onCalendars = pathname.startsWith('/app/booking/calendars');
  const action =
    'mt-3 inline-flex min-h-11 items-center rounded bg-red-700 px-4 py-2 font-medium text-white sm:min-h-0 sm:py-1.5';

  return (
    <div className="mb-6 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800">
      <p className="font-medium">{t('booking:calendars.blockedTitle')}</p>
      <p className="mt-1">
        {t('booking:calendars.blockedBody', {
          accounts: broken.map((c) => c.email).join(', '),
        })}
      </p>
      {only ? (
        <a href={`/api/v1/calendar/connect/${only.provider}`} className={action}>
          {t('booking:calendars.blockedAction')}
        </a>
      ) : onCalendars ? null : (
        <Link to="/app/booking/calendars" className={action}>
          {t('booking:calendars.blockedAction')}
        </Link>
      )}
    </div>
  );
}
